/**
 * La ficha de un cliente (spec 024 · FR-004).
 *
 * A la izquierda, los hilos por fecha. En el centro, los mensajes.
 * A la derecha, el paso a paso del bot.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { BotonEnviarPlantilla } from '../../components/EnviarPlantilla'
import { api } from '../../lib/api'
import { DetalleHiloCliente, ListaHilos, type HiloLista } from './cliente-hilo'
import { NotasCliente } from '../../components/NotasCliente'

interface ClienteFicha {
    id: number
    nombre: string
    telefono: string | null
    email: string | null
    eaCustomerId: number | null
    consentimiento: boolean
    ficticio: boolean
    citas: number | null
    proximaCita: string | null
}

interface RespuestaFicha {
    accountId: number
    canal: { alcanzable: boolean; motivo: string | null }
    cliente: ClienteFicha | null
    conversaciones: HiloLista[]
}

function proximaCorta(v: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2})/.exec(v)
    if (!m) return v
    const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00Z`)
    return `${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} · ${m[4]}`
}

export function ClienteFichaNegocio() {
    const { accountId, contactId } = useParams<{ accountId: string; contactId: string }>()
    const [params, setParams] = useSearchParams()
    const hiloElegido = params.get('hilo')

    const { data, isPending, isError } = useQuery<RespuestaFicha>({
        queryKey: ['cliente-ficha', accountId, contactId],
        queryFn: () => api.get<RespuestaFicha>(`/business/${accountId}/clientes/${contactId}`),
    })

    useEffect(() => {
        if (!data?.conversaciones.length || hiloElegido) return
        const primero = data.conversaciones[0]
        if (!primero) return
        setParams({ hilo: String(primero.id) }, { replace: true })
    }, [data, hiloElegido, setParams])

    const convId = hiloElegido ? Number(hiloElegido) : NaN
    const lista = `/business/${accountId}/clientes`

    if (isPending) {
        return (
            <Marco volverA={lista}>
                <p className="text-sm text-neutral-500">Cargando la ficha…</p>
            </Marco>
        )
    }
    if (isError || !data) {
        return (
            <Marco volverA={lista}>
                <p className="text-sm text-rose-400">No se encontró a este cliente.</p>
            </Marco>
        )
    }
    if (!data.canal.alcanzable) {
        return (
            <Marco volverA={lista}>
                <p className="text-sm font-medium text-amber-300">No se ha podido leer el canal</p>
                <p className="mt-1 text-xs text-amber-200/70">{data.canal.motivo}</p>
            </Marco>
        )
    }
    if (!data.cliente) {
        return (
            <Marco volverA={lista}>
                <p className="text-sm text-rose-400">No se encontró a este cliente.</p>
            </Marco>
        )
    }

    const c = data.cliente
    const conCitaSinAtar = c.eaCustomerId === null && c.citas !== null && c.citas > 0

    return (
        <Marco volverA={lista}>
            <header className="space-y-2 border-b border-neutral-800 pb-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-lg font-semibold text-neutral-100">{c.nombre}</h2>
                    <BotonEnviarPlantilla accountId={data.accountId} contactId={c.id} conversationId={null} />
                    {c.ficticio && (
                        <span className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-300">
                            de prueba
                        </span>
                    )}
                </div>
                <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                    <Dato etiqueta="Teléfono">
                        {c.telefono ?? <span className="text-neutral-600">sin teléfono</span>}
                    </Dato>
                    <Dato etiqueta="Correo">
                        {c.email ?? <span className="text-neutral-600">—</span>}
                    </Dato>
                    <Dato etiqueta="Condiciones">
                        {c.consentimiento ? 'aceptadas' : <span className="text-neutral-600">—</span>}
                    </Dato>
                    <Dato etiqueta="Agenda">
                        {c.eaCustomerId !== null ? (
                            <span className="text-emerald-400">atado</span>
                        ) : conCitaSinAtar ? (
                            <span className="text-amber-400">sin atar, y tiene cita</span>
                        ) : (
                            <span className="text-neutral-400">sin atar</span>
                        )}
                    </Dato>
                    <Dato etiqueta="Citas">
                        {c.citas === null ? (
                            <span className="text-amber-400">?</span>
                        ) : c.citas === 0 ? (
                            <span className="text-neutral-500">ninguna por delante</span>
                        ) : (
                            <span>
                                {c.citas}
                                {c.proximaCita && (
                                    <span className="ml-1.5 text-neutral-500">
                                        próx. {proximaCorta(c.proximaCita)}
                                    </span>
                                )}
                            </span>
                        )}
                    </Dato>
                </dl>
            </header>

            {accountId && (
                <NotasCliente accountId={accountId} contactId={c.id} />
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(12rem,16rem)_1fr]">
                <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Conversaciones
                    </h3>
                    <ListaHilos
                        hilos={data.conversaciones}
                        elegido={hiloElegido}
                        alElegir={(id) => setParams({ hilo: String(id) })}
                    />
                </section>
                {!Number.isInteger(convId) || convId <= 0 ? (
                    <p className="text-sm text-neutral-500">Elige una conversación.</p>
                ) : (
                    <DetalleHiloCliente
                        accountId={accountId}
                        contactId={contactId}
                        convId={convId}
                    />
                )}
            </div>
        </Marco>
    )
}

function Marco({ volverA, children }: { volverA: string; children: React.ReactNode }) {
    return (
        <div className="space-y-4">
            <Link
                to={volverA}
                className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-300"
            >
                ← Volver a clientes
            </Link>
            {children}
        </div>
    )
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
    return (
        <div>
            <dt className="text-[11px] uppercase tracking-wide text-neutral-500">{etiqueta}</dt>
            <dd className="mt-0.5 text-neutral-200">{children}</dd>
        </div>
    )
}
